/*══════════════════════════════════════════════════════════════
  src/LexMail.js · LEGO-Grid Edition (static layout 2025-06-28)
  → all tiles full-width, one per row
══════════════════════════════════════════════════════════════*/

import React, {
  useState, useContext, useEffect, useRef, memo, useCallback
} from "react";
import { FaEnvelope, FaTimes } from "react-icons/fa";
import { Responsive, WidthProvider } from "react-grid-layout";
import { AuthContext }           from "../user/AuthContext";
import { loadGmailAccounts,
         saveGmailAccounts }     from "../firebase";

import Inbox        from "./Inbox";
import SmartSummary from "./SmartSummary";
import BriefCall    from "./BriefCall";
import Angle2       from "./angle2";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./LexMail.css";

const API        = process.env.REACT_APP_API_URL || "https://lextrackai.com";
const RGL        = WidthProvider(Responsive);
// We'll still use 12 total columns, but each tile will take all 12
const COLS       = { lg:12, md:12, sm:12, xs:12, xxs:12 };
const BREAKPOINT = { lg:1200, md:900, sm:700, xs:480, xxs:0 };
const ROW_H      = 64;
const MAX_ROWS   = 20;   // plenty of room for stacking

/* One-column vertical layout */
const VERTICAL = [
  { i:"accounts", x:0,  y:0,  w:12, h:4,  minW:12, minH:3 },
  { i:"brief",    x:0,  y:4,  w:12, h:5,  minW:12, minH:4 },
  { i:"summary",  x:0,  y:9,  w:12, h:4,  minW:12, minH:3 },
  { i:"inbox",    x:0,  y:13, w:12, h:1,  static:true }
];

const uniq = arr => [...new Set(arr)].filter(Boolean);

const AccountsTile = memo(({ accounts, selected, onSelect, onAdd, onRemove })=>(
  <div className="tile tile-accounts">
    <h3 className="tile-handle">Lex Email</h3>
    <ul className="email-list">
      {accounts.map(e=>(
        <li key={e} className={e===selected ? "active" : undefined}>
          <span onClick={()=>onSelect(e)}><FaEnvelope/>{e}</span>
          <button title="Remove" onClick={ev=>{ev.stopPropagation();onRemove(e);}}>
            <FaTimes/>
          </button>
        </li>
      ))}
    </ul>
    <button className="add-account-btn" onClick={onAdd}>+ Add account</button>
  </div>
));

function LexMailInner(){
  const { user } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [chosen , setChosen ]   = useState(null);
  const [msgs   , setMsgs   ]   = useState({});

  // apply the same vertical layout at every breakpoint
  const [layouts, setLayouts] = useState({
    lg: VERTICAL,
    md: VERTICAL,
    sm: VERTICAL,
    xs: VERTICAL,
    xxs: VERTICAL
  });

  useEffect(()=>{
    if(!user?.uid) return;
    loadGmailAccounts(user.uid).then(raw=>{
      const list = uniq(raw.map(r=> typeof r==="string"?r:r.email ));
      setAccounts(list);
      if(list.length) setChosen(list[0]);
    });
  },[user?.uid]);

  const firstSave = useRef(true);
  useEffect(()=>{
    if(!user?.uid) return;
    if(firstSave.current){ firstSave.current=false; return; }
    saveGmailAccounts(accounts, user.uid);
  },[accounts,user?.uid]);

  useEffect(()=>{
    const handler = e => {
      const allow = [ window.location.origin, new URL(API).origin, "https://api.lextrackai.com" ];
      if(!allow.includes(e.origin)) return;
      if(e.data?.type==="gmail-linked"){
        setAccounts(a=>uniq([...a,e.data.email]));
        setChosen(e.data.email);
        fetchInbox(e.data.uid, e.data.email);
      }
    };
    window.addEventListener("message", handler);
    return()=>window.removeEventListener("message", handler);
  },[]);

  useEffect(()=>{
    if(user?.uid && chosen) fetchInbox(user.uid, chosen);
  },[user?.uid,chosen]);

  const fetchInbox = async(uid,email)=>{
    try {
      const res = await fetch(
        `${API}/api/gmail/messages?uid=${encodeURIComponent(uid)}&email=${encodeURIComponent(email)}`
      );
      const data = await res.json();
      if(data.error==="need_auth") return alert(`Please re-link Gmail: ${email}`);
      setMsgs(m=>({...m,[email]: data.messages||[]}));
    } catch(err){ console.error(err); }
  };

  const onLayoutChange = useCallback((_,all)=>{
    setLayouts(all);
    localStorage.setItem("lexmail_layout",JSON.stringify(all));
  },[]);

  const list = msgs[chosen]||[];
  const summary = list.map(m=>({
    id:      m.id,
    subject: m.headers?.Subject||"(no subject)",
    body:    m.snippet
  }));

  return (
    <div className="lexmail-inner">
      <RGL
        className="lego-grid"
        layouts={layouts}
        breakpoints={BREAKPOINT}
        cols={COLS}
        rowHeight={ROW_H}
        maxRows={MAX_ROWS}
        margin={[8,8]}
        isDraggable={false}
        isResizable={false}
        compactType={null}
        autoSize
        onLayoutChange={onLayoutChange}
      >
        <div key="accounts">
          <AccountsTile
            accounts={accounts}
            selected={chosen}
            onSelect={setChosen}

           onAdd={() => {
  if (!user?.uid) {
    alert("You need to login first");
    return;
  }
  window.open(
    `${API}/api/google/link?uid=${user.uid}`,
    "_blank", "width=500,height=700"
  );
}}


            onRemove={email=>{
              setAccounts(a=>a.filter(e=>e!==email));
              if(email===chosen) setChosen(a=>a.find(x=>x!==email)||null);
            }}
          />
        </div>

        <div key="brief">
          <div className="tile">
            <div className="tile-body">
              <BriefCall items={summary}/>
            </div>
          </div>
        </div>

        <div key="summary">
          <div className="tile">
            <div className="tile-body">
              <SmartSummary items={summary}/>
            </div>
          </div>
        </div>

        <div key="inbox" style={{display:"none"}}/>
      </RGL>
    </div>
  );
}

export default function LexMail(){
  return (
    <Angle2 transparent>
      <LexMailInner/>
    </Angle2>
  );
}
